import { useReactiveVar } from '@apollo/client';
import { Box, Card, CardContent, keyframes, Skeleton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import { getDensityConfig } from 'src/helpers/densityConfig';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    margin: 'auto',
    width: '100%',
    height: 'auto',
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  thumbnailArea: {
    width: '100%',
    aspectRatio: '16/9',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    backgroundImage: 'linear-gradient(90deg, transparent 25%, rgba(255, 255, 255, 0.05) 50%, transparent 75%)',
    backgroundSize: '400% 100%',
    animation: `${shimmer} 2s ease-in-out infinite`,
  },
  cardContent: {
    padding: '8px 12px 0 12px',
  },
  fieldRow: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    padding: '1px 0',
    margin: '0 0 2px 0',
  },
}));

const waveSx = {
  bgcolor: 'rgba(255, 255, 255, 0.06)',
  borderRadius: 0.5,
  '&::after': {
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)',
  },
};

const dimWaveSx = {
  ...waveSx,
  bgcolor: 'rgba(255, 255, 255, 0.04)',
};

const allFields = [
  { labelWidth: '25%', valueWidth: '75%' },
  { labelWidth: '35%', valueWidth: '50%' },
  { labelWidth: '20%', valueWidth: '65%' },
];

const SkeletonMediaCard: React.FC = () => {
  const classes = useStyles();
  const { density } = useReactiveVar(akomiMediaCardGridVar);
  const densityConfig = getDensityConfig(density);
  const fields = allFields.slice(0, densityConfig.skeletonFieldCount);

  return (
    <Card raised className={classes.card}>
      {/* Thumbnail shimmer */}
      <Box className={classes.thumbnailArea} />

      {/* Metadata skeleton */}
      <CardContent className={classes.cardContent} sx={{ padding: densityConfig.cardContentPadding }}>
        <Skeleton animation="wave" variant="text" width="55%" sx={{ height: 18, mb: 0.5, ...waveSx }} />
        {fields.map((field, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton layout
          <Box key={i} className={classes.fieldRow}>
            <Skeleton animation="wave" variant="text" width={field.labelWidth} sx={{ height: 14, ...dimWaveSx }} />
            <Skeleton animation="wave" variant="text" width={field.valueWidth} sx={{ height: 16, ...waveSx }} />
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

export default SkeletonMediaCard;
